import { useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';

import { ApiError } from '@mds/shared/lib/api';
import type { AdminRole, AdminUser } from '@mds/shared/types/admin';

import * as admin from '@/administration/administration.api';
import { statusTone } from '@/administration/status-tone';
import RolePicker from '@/components/administration/role-picker';
import Button from '@/components/ui/button';
import Chip from '@/components/ui/chip';
import { roleIdsSchema } from '@/domain/administrators';
import { stamp } from '@mds/shared/lib/dates';
import { actionLabel, actorLabel } from '@/lib/labels';
import { useValidation } from '@/lib/validation';

/** What replacing an administrator's roles sends. */
const rolesSchema = z.object({ roleIds: roleIdsSchema });

/** One titled block of the record. */
function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="flex flex-col gap-3">
      <h3 className="m-0 border-b border-hairline pb-2 font-mono text-md font-semibold tracking-wider text-brand-text uppercase">
        {title}
      </h3>
      {children}
    </section>
  );
}

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="font-mono text-sm tracking-wide text-ink-muted uppercase">{label}</dt>
      <dd className="m-0">{children}</dd>
    </>
  );
}

/**
 * Everything held on one administrator, plus the two actions that do not
 * belong in the editor: replacing roles and ending their sessions.
 */
export default function AdminUserRecord({
  user,
  roles,
  canManage,
  isSelf,
  onChanged,
}: {
  user: AdminUser;
  roles: AdminRole[];
  canManage: boolean;
  isSelf: boolean;
  onChanged: (updated: AdminUser) => void | Promise<void>;
}) {
  const [selected, setSelected] = useState<string[]>(user.roles.map((r) => r.id));
  const [busy, setBusy] = useState<'roles' | 'sessions' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const draft = useMemo(() => ({ roleIds: selected }), [selected]);
  const validation = useValidation(rolesSchema, draft);

  const timeline = useQuery({
    queryKey: ['admin', 'audit', 'user', user.id],
    queryFn: () => admin.auditTimeline('user', user.id, 10),
  });

  const dirty =
    selected.length !== user.roles.length ||
    selected.some((id) => !user.roles.some((r) => r.id === id));

  const act = async (kind: 'roles' | 'sessions', run: () => Promise<void>) => {
    setBusy(kind);
    setError(null);
    setNote(null);
    try {
      await run();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work. Try again.');
    } finally {
      setBusy(null);
    }
  };

  const saveRoles = () => {
    if (!validation.check()) return;
    return act('roles', async () => {
      const updated = await admin.replaceRoles(user.id, selected);
      setNote('Roles updated.');
      await onChanged(updated);
    });
  };

  const endSessions = () =>
    act('sessions', async () => {
      const { revokedSessionCount } = await admin.revokeSessions(user.id);
      setNote(
        revokedSessionCount === 0
          ? 'There were no active sessions to end.'
          : `Ended ${revokedSessionCount} session${revokedSessionCount === 1 ? '' : 's'}.`,
      );
    });

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap gap-2">
        <Chip className={'border ' + statusTone(user.status)}>{user.status}</Chip>
        {user.roles.map((r) => (
          <Chip key={r.id}>{r.name}</Chip>
        ))}
        {isSelf && (
          <Chip className="border border-brand-edge/40 text-brand-strong">Your account</Chip>
        )}
      </div>

      <Section title="Account">
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-5 gap-y-3 text-base">
          <Line label="Email">
            <span className="break-all">{user.email}</span>
          </Line>
          <Line label="Phone">
            <span className="font-mono">{user.phone ?? '—'}</span>
          </Line>
          <Line label="Email verified">
            <span className="font-mono">{stamp(user.emailVerifiedAt)}</span>
          </Line>
          <Line label="Last sign-in">
            <span className="font-mono">{stamp(user.lastLoginAt)}</span>
          </Line>
          <Line label="Created">
            <span className="font-mono">{stamp(user.createdAt)}</span>
          </Line>
        </dl>
      </Section>

      {canManage && (
        <Section title="Roles">
          <RolePicker
            roles={roles}
            value={selected}
            onChange={setSelected}
            // The server refuses to strip super_admin from your own account,
            // so the control says so rather than letting it fail on submit.
            locked={(r, on) =>
              isSelf && r.key === 'super_admin' && on
                ? 'You cannot remove this role from your own account'
                : null
            }
            {...validation.field('roleIds', { within: true })}
          />

          <div className="flex flex-wrap items-center gap-3">
            <Button disabled={!dirty || busy !== null} onClick={() => void saveRoles()}>
              {busy === 'roles' ? 'Saving…' : 'Save roles'}
            </Button>
          </div>
        </Section>
      )}

      {canManage && (
        <Section title="Sessions">
          <p className="m-0 text-base text-ink-muted">
            Ends every signed-in session for this administrator. They will have to sign in again.
          </p>
          <div>
            <Button variant="danger" disabled={busy !== null} onClick={endSessions}>
              {busy === 'sessions' ? 'Ending…' : 'End all sessions'}
            </Button>
          </div>
        </Section>
      )}

      {(error || note) && (
        <p
          role={error ? 'alert' : undefined}
          className={
            'm-0 rounded-xl border px-4 py-3 text-base ' +
            (error
              ? 'border-danger-edge bg-danger-wash text-danger-text'
              : 'border-brand-edge/40 bg-brand-wash text-brand-strong')
          }
        >
          {error ?? note}
        </p>
      )}

      <Section title="Recent activity">
        {timeline.isPending ? (
          <p className="m-0 text-base text-ink-muted">Loading…</p>
        ) : timeline.isError || (timeline.data ?? []).length === 0 ? (
          <p className="m-0 text-base text-ink-muted">Nothing recorded against this account.</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {timeline.data!.map((entry) => (
              <li
                key={entry.id}
                className="flex flex-wrap items-baseline justify-between gap-3 border-b border-hairline/70 pb-2 text-base"
              >
                <span className="text-base">{actionLabel(entry.action)}</span>
                <span className="font-mono text-sm text-ink-muted">
                  {entry.actor.email ?? actorLabel(entry.actor.type)} · {stamp(entry.createdAt)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}
